var _excluded = ["backgroundImage", "logo", "label", "title", "titleHtmlTag", "link", "description", "parameters", "price", "hasSubgrid"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var s = Object.getOwnPropertySymbols(e); for (r = 0; r < s.length; r++) o = s[r], t.includes(o) || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.includes(n)) continue; t[n] = r[n]; } return t; }
import React, { useContext } from "react";
import { cover } from "polished";
import styled, { css } from "styled-components";
import OnColorContext from "../../../contexts/OnColorContext";
import ConditionalWrapper from "../../../helpers/ConditionalWrapper";
import ResizeDetector from "../../../helpers/ResizeDetector";
import getBorderRadius from "../../../utils/getBorderRadius";
import getBoxShadow from "../../../utils/getBoxShadow";
import getColor from "../../../utils/getColor";
import isNotEmpty from "../../../utils/isNotEmpty";
import space from "../../../utils/space";
import transition from "../../../utils/transition";
import unit from "../../../utils/unit";
import LabelAtm from "../../atoms/LabelAtm";
import { labelSizes, labelTypography } from "../../atoms/LabelAtm/LabelAtm.config";
import LogoBoxAtm, { logoBoxAtmSizes } from "../../atoms/LogoBoxAtm";
import TextAtm from "../../atoms/TextAtm";
import ProductCardsSliderOrgContext from "../../organisms/ProductCardsSliderOrg/ProductCardsSliderOrgContext";
import Link from "../../quarks/Link";
import Picture from "../../quarks/Picture";
export var defaultProps = {
  titleHtmlTag: "h2"
};
var paddingSpace = 2;
var backgroundColor = "backgroundLight";
var logoBoxSize = "small";
var labelAtmSize = "default";
var labelGutterSpace = 1;
var HeaderBackground = styled.div.withConfig({
  componentId: "sc-ea7dlu-0"
})(["width:calc(100% + ", ");height:", ";margin:", " ", " 0 ", ";overflow:hidden;"], space(2 * paddingSpace), space(10), space(-1 * paddingSpace), space(-1 * paddingSpace), space(-1 * paddingSpace));
var HeaderBackgroundImage = styled(Picture).withConfig({
  componentId: "sc-ea7dlu-1"
})(["", ";width:100%;height:100%;object-fit:cover;"], transition("transform"));
var Container = styled.article.withConfig({
  componentId: "sc-ea7dlu-2"
})(function (_ref) {
  var $hasSubgrid = _ref.$hasSubgrid,
    theme = _ref.theme;
  return css(["", ";", " position:relative;padding:", ";overflow:hidden;border-radius:", ";background-color:", ";box-shadow:", ";&:hover{box-shadow:", ";", "{transform:scale(1.2);}}min-width:", ";"], transition("box-shadow"), $hasSubgrid ? css(["display:grid;grid-template-rows:subgrid;grid-row:auto / span 3;grid-gap:", ";"], space(1)) : css(["display:flex !important;flex-direction:column;"]), space(paddingSpace), getBorderRadius(theme, "large"), getColor(theme, backgroundColor), getBoxShadow(theme, "small"), getBoxShadow(theme, "large"), HeaderBackgroundImage, space(40));
});
var ProductCardContent = styled.div.withConfig({
  componentId: "sc-ea7dlu-3"
})(function (_ref2) {
  var $minHeight = _ref2.$minHeight;
  return css(["", ""], $minHeight ? css(["min-height:", ";"], unit($minHeight)) : null);
});
var Header = styled.header.withConfig({
  componentId: "sc-ea7dlu-4"
})(function (_ref3) {
  var $hasSubgrid = _ref3.$hasSubgrid;
  return css(["", ""], !$hasSubgrid ? css(["margin-bottom:", ";"], space(2)) : null);
});
var HeaderLogoContainer = styled.div.withConfig({
  componentId: "sc-ea7dlu-5"
})(function (_ref4) {
  var theme = _ref4.theme;
  var logoBoxHeightSpace = logoBoxAtmSizes[logoBoxSize].boxSizeSpace;
  var labelAtmPaddingSpace = labelSizes[labelAtmSize].paddingSpace;
  var labelAtmLineHeightSpace = theme.typography[labelTypography].lineHeightSpace;
  var labelAtmHeightSpace;
  if (typeof labelAtmPaddingSpace === "number") {
    labelAtmHeightSpace = 2 * labelAtmPaddingSpace + labelAtmLineHeightSpace;
  } else {
    labelAtmHeightSpace = 2 * labelAtmPaddingSpace.y + labelAtmLineHeightSpace;
  }
  return css(["display:flex;position:relative;align-items:flex-end;justify-content:space-between;margin-top:", ";margin-bottom:", ";"], space(-1 * logoBoxHeightSpace + labelAtmHeightSpace + labelGutterSpace), space(1.5));
});
var Title = styled(TextAtm).attrs(function (_ref5) {
  var _ref5$htmlTag = _ref5.htmlTag,
    htmlTag = _ref5$htmlTag === void 0 ? defaultProps.titleHtmlTag : _ref5$htmlTag;
  return {
    htmlTag: htmlTag,
    typography: "big3"
  };
}).withConfig({
  componentId: "sc-ea7dlu-6"
})(["> *{color:inherit;text-decoration:none;&::after{", ";content:\"\";}}"], cover());
var Info = styled.div.withConfig({
  componentId: "sc-ea7dlu-7"
})(["margin-bottom:", ";"], space(2));
var PriceContainer = styled.div.withConfig({
  componentId: "sc-ea7dlu-8"
})(["display:flex;align-items:baseline;> :last-child{flex:1;}"]);
function ProductCardMol(_ref6) {
  var backgroundImage = _ref6.backgroundImage,
    logo = _ref6.logo,
    label = _ref6.label,
    title = _ref6.title,
    _ref6$titleHtmlTag = _ref6.titleHtmlTag,
    titleHtmlTag = _ref6$titleHtmlTag === void 0 ? defaultProps.titleHtmlTag : _ref6$titleHtmlTag,
    link = _ref6.link,
    description = _ref6.description,
    parameters = _ref6.parameters,
    price = _ref6.price,
    hasSubgrid = _ref6.hasSubgrid,
    restProps = _objectWithoutProperties(_ref6, _excluded);
  var _useContext = useContext(ProductCardsSliderOrgContext),
    productCardContent = _useContext.productCardContent;
  return /*#__PURE__*/React.createElement(Container, _extends({
    $hasSubgrid: hasSubgrid
  }, restProps, {
    "data-test-id": "product-card"
  }), /*#__PURE__*/React.createElement(OnColorContext.Provider, {
    value: backgroundColor
  }, /*#__PURE__*/React.createElement(ConditionalWrapper, {
    condition: !hasSubgrid,
    wrapper: function wrapper(children) {
      return /*#__PURE__*/React.createElement(ResizeDetector, {
        setHeight: productCardContent.setMaxHeight
      }, /*#__PURE__*/React.createElement(ProductCardContent, {
        $minHeight: productCardContent.maxHeight
      }, children));
    }
  }, /*#__PURE__*/React.createElement(Header, {
    $hasSubgrid: hasSubgrid
  }, /*#__PURE__*/React.createElement(HeaderBackground, null, /*#__PURE__*/React.createElement(HeaderBackgroundImage, {
    srcData: backgroundImage,
    alt: backgroundImage.alt
  })), /*#__PURE__*/React.createElement(HeaderLogoContainer, null, /*#__PURE__*/React.createElement(LogoBoxAtm, _extends({}, logo, {
    size: logoBoxSize
  })), isNotEmpty(label) ? /*#__PURE__*/React.createElement(LabelAtm, {
    size: labelAtmSize,
    variant: label.variant || "notify",
    emphasis: label.emphasis || "high"
  }, label.children) : null), /*#__PURE__*/React.createElement(Title, {
    htmlTag: titleHtmlTag
  }, /*#__PURE__*/React.createElement(Link, link, title))), description || parameters ? /*#__PURE__*/React.createElement(Info, null, description ? /*#__PURE__*/React.createElement(TextAtm, {
    typography: "tiny1",
    emphasis: "medium",
    pushSpace: 0.5
  }, description) : null, parameters ? /*#__PURE__*/React.createElement(TextAtm, {
    typography: "tiny1"
  }, parameters) : null) : null), /*#__PURE__*/React.createElement("footer", null, /*#__PURE__*/React.createElement(PriceContainer, null, /*#__PURE__*/React.createElement("div", {
    "data-test-id": "product-card-regular-price"
  }, price)))));
}
export default ProductCardMol;